import {
  ContextMenu,
  ContextMenuOption,
  Tabs,
  Theme,
} from '@infotech-soe/erp-ui-kit';
import React, { DragEvent, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import UniqueId from '../../../services/uid/UniqueId';
import { OpenContentEntity } from '../../../store/slices/content';
import { useContextMenu } from '../../hooks/useContextMenu';

import ContentEntity from './ContentEntity';

interface ContentTabEntity extends OpenContentEntity {
  canPin?: boolean;
}

const messages = defineMessages({
  pin: {
    id: 'core.contentTab.pin',
    defaultMessage: 'Pin',
  },
  unpin: {
    id: 'core.contentTab.unpin',
    defaultMessage: 'Unpin',
  },
  close: {
    id: 'core.contentTab.close',
    defaultMessage: 'Close category',
  },
});

const useStyles = createUseStyles(({ colors }: Theme) => ({
  dragover: {
    position: 'relative',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: 2,
      background: colors.primary,
    },
  },
}));

export interface DispatchProps {
  onTabActivate: (id: UniqueId) => void;
  onTabClose: (id: UniqueId) => void;
  onTabTogglePin: (id: UniqueId) => void;
  onTabReorder: (fromIndex: number, toIndex: number) => void;
}

export interface StateProps {
  entities: ContentTabEntity[];
  activeTab: UniqueId | null;
}

const ContentTabs: React.FC<DispatchProps & StateProps> = ({
  entities,
  onTabClose,
  activeTab,
  onTabActivate,
  onTabTogglePin,
  onTabReorder,
}) => {
  const { formatMessage } = useIntl();
  const contextMenu = useContextMenu();
  const classes = useStyles();
  const [contextMenuTabId, setContextMenuTabId] = useState<UniqueId | null>(
    null
  );
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleContextMenu = useCallback(
    (id: UniqueId) =>
      ({ clientX, clientY }: React.MouseEvent) => {
        setContextMenuTabId(id);
        contextMenu.setPositionX(clientX);
        contextMenu.setPositionY(clientY);
        contextMenu.open();
      },
    [contextMenu]
  );

  const handleMenuClick = useCallback(
    (action: string) => {
      if (!contextMenuTabId) return;

      if (action === 'close') {
        onTabClose(contextMenuTabId);
      } else if (action === 'togglePin') {
        onTabTogglePin(contextMenuTabId);
      }
      contextMenu.close();
    },
    [contextMenuTabId, onTabClose, onTabTogglePin, contextMenu]
  );

  const handleTabCloseOrUnpin = useCallback(
    (id: UniqueId) => {
      const entity = entities.find(e => e.id === id);
      if (entity?.pinned) {
        onTabTogglePin(id);
      } else {
        onTabClose(id);
      }
    },
    [entities, onTabTogglePin, onTabClose]
  );

  const handleDragStart =
    (index: number) => (_: UniqueId, event: DragEvent<HTMLButtonElement>) => {
      event.dataTransfer.effectAllowed = 'move';
      setDraggedIndex(index);
      event.currentTarget.style.opacity = '0.5';
    };

  const handleDragOver =
    (index: number) => (_: UniqueId, event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDragOverIndex(index);
    };

  const handleDragLeave = (
    _: UniqueId,
    event: DragEvent<HTMLButtonElement>
  ) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!event.currentTarget.contains(relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop =
    (index: number) => (_: UniqueId, event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (draggedIndex !== null && draggedIndex !== index) {
        onTabReorder(draggedIndex, index);
      }
      setDragOverIndex(null);
    };

  const handleDragEnd = (_: UniqueId, event: DragEvent<HTMLButtonElement>) => {
    event.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const contextMenuOptions = useMemo<Array<ContextMenuOption<string>>>(() => {
    const currentEntity = entities.find(e => e.id === contextMenuTabId);
    return [
      {
        type: 'listitem',
        label: formatMessage(messages.close),
        value: 'close',
        icon: 'cross',
        disabled: currentEntity?.pinned,
      },
      { type: 'separator' },
      {
        type: 'listitem',
        label: formatMessage(
          currentEntity?.pinned ? messages.unpin : messages.pin
        ),
        value: 'togglePin',
        icon: 'attach',
        disabled: !currentEntity?.canPin,
      },
    ];
  }, [contextMenuTabId, entities, formatMessage]);

  return entities.length > 0 ? (
    <>
      <Tabs
        active={activeTab ?? undefined}
        onTabActivate={(id: UniqueId) => activeTab !== id && onTabActivate(id)}
        onTabClose={handleTabCloseOrUnpin}
        headerContainerClassName="data-view-pane"
      >
        {entities.map((entity, index) => (
          <Tabs.Pane
            key={entity.id}
            name={entity.id}
            label={entity.title}
            withCloseButton
            closeIcon={entity.pinned ? 'attach' : 'cross'}
            draggable
            buttonClassName={
              dragOverIndex === index ? classes.dragover : undefined
            }
            onContextMenu={handleContextMenu(entity.id)}
            onDragStart={handleDragStart(index)}
            onDragOver={handleDragOver(index)}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop(index)}
            onDragEnd={handleDragEnd}
          >
            <ContentEntity
              contentType={entity.type}
              contentId={entity.content}
            />
          </Tabs.Pane>
        ))}
      </Tabs>
      <ContextMenu
        x={contextMenu.positionX}
        y={contextMenu.positionY}
        open={contextMenu.isOpen}
        options={contextMenuOptions}
        onOpen={() => contextMenu.close()}
        onClick={handleMenuClick}
      />
    </>
  ) : null;
};

export default ContentTabs;
